// Media Center upload constraints — driven by admin settings (no longer static).
//
// The BE exposes two upload settings on the PUBLIC settings endpoint so the uploader can
// read them without `SettingsRead`:
//   • upload.maxFileSizeMb   (Int)  — effective per-file size limit
//   • upload.allowedExtensions (Json array of extensions, e.g. ["png","pdf"])
//
// This module is the single pure place that turns those raw settings into the typed values
// the uploader needs. It always returns a valid object — falling back to safe defaults when a
// setting is missing/corrupt — so the uploader keeps working even if the BE is unreachable.

import type { PublicSetting } from '@features/settings/types';

/** Fallback per-file limit (MB) when the setting is missing — mirrors BE UploadConstraints. */
export const DEFAULT_MAX_FILE_MB = 25;
/** Hard ceiling (MB) the configurable limit is clamped to — mirrors BE MaxConfigurableMb. */
export const MAX_CONFIGURABLE_MB = 500;
/** Fallback allow-list when the setting is missing — mirrors BE DefaultExtensions. */
export const DEFAULT_ALLOWED_EXTENSIONS = [
  'png', 'jpg', 'jpeg', 'webp', 'gif', 'pdf', 'doc', 'docx', 'xls', 'xlsx', 'txt', 'csv',
];

export interface UploadConstraints {
  /** Effective per-file limit, in bytes. */
  maxFileBytes: number;
  /** Effective per-file limit, in MB (for labels). */
  maxFileMb: number;
  /** Normalized (lower-case, no leading dot) allow-listed extensions. */
  allowedExtensions: string[];
  /** Value for the `<input accept>` / AntD Upload `accept` prop, e.g. ".png,.pdf". */
  accept: string;
}

export const MEDIA_UPLOAD_SETTING_KEYS = {
  maxFileSizeMb: 'upload.maxFileSizeMb',
  allowedExtensions: 'upload.allowedExtensions',
} as const;

function clamp(n: number, min: number, max: number): number {
  return Math.min(Math.max(n, min), max);
}

function normalizeExt(raw: unknown): string {
  if (typeof raw !== 'string') return '';
  return raw.trim().replace(/^\./, '').toLowerCase();
}

function parseMaxFileMb(settings: PublicSetting[]): number {
  const raw = settings.find((s) => s.key === MEDIA_UPLOAD_SETTING_KEYS.maxFileSizeMb)?.value;
  // Missing/non-numeric → historical default. A finite-but-out-of-range value is clamped to
  // [1, ceiling] (mirrors the BE Math.Clamp), so 0 / negative become 1 rather than the default.
  if (raw == null || raw.trim() === '') return DEFAULT_MAX_FILE_MB;
  const n = Number(raw);
  if (!Number.isFinite(n)) return DEFAULT_MAX_FILE_MB;
  return clamp(Math.floor(n), 1, MAX_CONFIGURABLE_MB);
}

function parseAllowedExtensions(settings: PublicSetting[]): string[] {
  const raw = settings.find((s) => s.key === MEDIA_UPLOAD_SETTING_KEYS.allowedExtensions)?.value;
  if (raw) {
    try {
      const parsed: unknown = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        const exts = Array.from(
          new Set(parsed.map(normalizeExt).filter((e) => e.length > 0)),
        );
        if (exts.length > 0) return exts;
      }
    } catch {
      // fall through to defaults
    }
  }
  return [...DEFAULT_ALLOWED_EXTENSIONS];
}

/** Build the constraints object from the public settings list (defaults when empty/missing). */
export function parseUploadConstraints(settings: PublicSetting[]): UploadConstraints {
  const maxFileMb = parseMaxFileMb(settings);
  const allowedExtensions = parseAllowedExtensions(settings);
  return {
    maxFileBytes: maxFileMb * 1024 * 1024,
    maxFileMb,
    allowedExtensions,
    accept: allowedExtensions.map((e) => `.${e}`).join(','),
  };
}

/** True when the file's extension is allow-listed (empty list = accept anything). */
export function isExtensionAllowed(fileName: string, allowedExtensions: string[]): boolean {
  if (allowedExtensions.length === 0) return true;
  const dot = fileName.lastIndexOf('.');
  if (dot < 0 || dot === fileName.length - 1) return false;
  return allowedExtensions.includes(fileName.slice(dot + 1).toLowerCase());
}
